import React from 'react';
import { redirect } from '../../../helper/redirectHelper';
import { Wrapper, Title, SubTitle } from './APIOverviewPage.styled';
import Terms from '../../components/common/Terms';

const APIOverviewPage = () => {
    return (
        <Wrapper className="container">
            <Title data-testid="title">API overview</Title>
            <p>
                The EPO Linked Open EP Data service provides a web based API for querying and accessing the EP data. The
                API is intended for occasional light use
            </p>
            <p>
                The API enables data about specific resources such as an application, a publication or a vocabulary term
                to be retrieved. Simply use a web browser or other tool to dereference the URL that identifies the
                desired resource.
            </p>
            <p>For example, clicking on this URL</p>
            <p>
                <a data-testid="exampleApplication" className="link" href={redirect('doc/application/EP/81850042')}>
                    https://data.epo.org/linked-data/doc/application/EP/81850042
                </a>
            </p>
            <p>will retrieve data about the EP patent with application number 81850042.</p>
            <p>
                By default, a browser will specify to the API that it prefers to receive data in HTML format. By adding
                a short extension to the URL, the same data may be retrieved in a different format. For example
            </p>
            <div>
                <a className="link" href={redirect('doc/application/EP/81850042.json')}>
                    https://data.epo.org/linked-data/doc/application/EP/81850042.json
                </a>
            </div>
            <div>
                <a className="link" href={redirect('doc/application/EP/81850042.ttl')}>
                    https://data.epo.org/linked-data/doc/application/EP/81850042.ttl
                </a>
            </div>
            <p>
                will retrieve the data as JSON and TURTLE formats respectively. JSON is a data format commonly used by
                web developers. TURTLE is a format commonly used to represent linked data. Alternatively, instead of
                specifying the format required in the URL, HTTP content negotiation may be used for the requester to
                agree the data format with the server.
            </p>
            <p>
                Most of the features of the API are available through the features of the HTML pages returned by the
                API. A common way to become familiar with the API is to use the HTML pages to browse the data. Start
                with one of the endpoints listed in the menu bar above, and use the features of the retrieved pages to
                generate new requests to the API.
            </p>
            <p>
                The API conforms to the <a href="https://code.google.com/archive/p/linked-data-api/">Linked Data API</a>{' '}
                specification. A list of API endpoints and example API requests can be found in the{' '}
                <a href="documentation/api-reference">API Reference Documentation</a>.
            </p>
            <SubTitle data-testid="subTitle">API Concepts</SubTitle>
            <h3 data-testid="endpoints">Endpoints</h3>
            <p>
                The API is divided into endpoints, each identified by a URI. An API request to an endpoint consists of
                an HTTP request, either a GET or a POST, to the endpoint's URI. The request may be modified by adding
                query parameters to the URI. Standard HTTP protocol features such as content negotiation are supported.
            </p>
            <p>
                There are two kinds of endpoints. Item endpoints are used to retrieve data about a single resource such
                as a patent application and List endpoints are used to retrieve data about multiple resources. The
                resources to be selected are defined by the endpoint and can be filtered by adding query parameters.
            </p>
            <h3 data-testid="views">Views</h3>
            <p>
                The data retrieved about each resource is determined by a view. Each endpoint has a default view, but
                this can be overridden by adding a query parameter to the request URL, for example:
            </p>
            <a data-testid="viewsLink" className="link" href={redirect('data/publication/EP/0084638/A1/-?_view=basic')}>
                https://data.epo.org/linked-data/data/publication/EP/0084638/A1/-.html?_view=basic
            </a>
            <p>The following views are defined in the API:</p>
            <h4>basic</h4>
            <p>
                This is a standard viewer. It returns minimal basic information about a resource, its URI, its type and
                label properties.
            </p>
            <h4>describe</h4>
            <p>
                This is also a standard viewer. It returns the values of all properties of a resource. If the value of
                any property is a so called anonymous resource, i.e. a resource that has no identifying URI, then the
                properties of that resource will also be returned. Adding the properties of anonymous resources is
                applied recursively. If any property of an anonymous property value itself has properties with anonymous
                values, then the properties of those anonymous resources are also returned, and so on. However, the
                properties of any anonymous resource will be returned only once in each request
            </p>
            <h4>all</h4>
            <p>
                A standard viewer. Like the describe viewer, but also retrieves the label properties of all resources
                returned.
            </p>
            <h4>normal</h4>
            <p>
                A customised view for an individual endpoint. Supported by endpoints which do not return a lot of data
                for each resource.
            </p>
            <h4>short</h4>
            <p>
                A customised view for an individual endpoint. Supported by endpoints that return resources with a lot of
                properties, in which case the short view is normally the default view for a list endpoint.
            </p>
            <h4>full</h4>
            <p>
                A customised view for an individual endpoint. Supported by endpoints that return resources with a lot of
                properties, in which case the full view is normally the default view for for an item endpoint.
            </p>
            <h4>none</h4>
            <p>
                An empty view that returns no data. A request specifying the none view will return only data specified
                in request query parameters. Properties can be added to a view in the api request, so the 'none' view
                allows the requester complete control over what data is returned.
            </p>
            <h3 data-testid="queryParameters">Query Parameters</h3>
            <p>Requests can be modified by adding query parameters to the request URL.</p>
            <h4>Filters</h4>
            <p>The resources returned by a request can be filtered. For example, the request</p>
            <a data-testid="filtersLink" className="link" href={redirect('doc/application')}>
                https://data.epo.org/doc/application
            </a>
            <p>retrieves a list of applications. This can be restricted to EP applications by using a filter:</p>
            <a
                className="link"
                href={redirect('doc/application?applicationAuthority=https://data.epo.org/doc/st3/EP')}
            >
                https://data.epo.org/doc/application?applicationAuthority=https://data.epo.org/linked-data/id/st3/EP
            </a>
            <p>
                This request returns only applications where the authority is the EPO. In this example, the first term
                in the query parameter 'applicationAuthority' is a short name. A list of of short names for this api can
                found in the <a href="documentation/api-reference#shortnames">api reference page</a>. The URL after the
                "=" is the URL used in the data to identify the EPO.
            </p>
            <p>Short names can combined to produce a path to a property value. For example,</p>
            <a className="link" href={redirect('doc/application?applicationAuthority.notation=EP')}>
                https://data.epo.org/linked-data/doc/application?applicationAuthority.notation=EP
            </a>
            <p>
                is an alternative API request that also returns a list of application with an applicationAuthority of
                the EPO.
            </p>
            <p>Where the value of a property has a natural sort order, min and max filters can be used. For example:</p>
            <a className="link" href={redirect('doc/application?min-filingDate=2000-01-01')}>
                https://data.epo.org/linked-data/doc/application?min-filingDate=2000-01-01
            </a>
            <p>
                returns applications which were filed on or after the 1st day of the millenium. To create a minimum or
                maximum value filter, prepend "min-" or "max-" respectively to a short name or path of short-names.
                Multiple filters can be specified in a request by specifying multiple parameters. This example specifies
                a range of filing dates:
            </p>
            <p>
                <a
                    className="link"
                    href={redirect('doc/application?min-filingDate=2000-01-01&max-filingDate=2000-12-31')}
                >
                    https://data.epo.org/linked-data/doc/application?min-filingDate=2000-01-01&max-filingDate=2000-12-31
                </a>
            </p>
            <h4>Pagination</h4>
            <p>
                The number of resources that can be retrieved from a single request to a list endpoint is limited. The
                default limit is 10, but this can be increased up to a limit of 200 by setting the _page_size=nnn query
                parameter. For example, set the page size to 50
            </p>
            <a className="link" href={redirect('doc/application?_pageSize=50')}>
                https://data.epo.org/linked-data/doc/application?_pageSize=50
            </a>
            <p>To retrieve the next 50 resources also set the _page parameter to 2</p>
            <a className="link" href={redirect('doc/application?_pageSize=50&_page=2')}>
                https://data.epo.org/linked-data/doc/application?_pageSize=50&_page=2
            </a>
            <p>This feature can be used to page through a larger result set.</p>
            <h3>Sorting</h3>
            <p>
                Data retrieved from a list endpoint can be sorted by specifying an _sort query parameter. In practice,
                this feature can only be used if the total number of resources selected by the request is small because
                sorting large amounts of data takes a lot of processing. Requests to sort large amounts of data will
                likely time out.
            </p>
            <p>
                This URL will retrieve EPO patent applications with an application date in the month of January 2016
                sorted by application number.
            </p>
            <a
                className="link"
                href={redirect(
                    'doc/application?applicationAuthority.notation=EP&min-filingDate=2016-01-01&max-filingDate=2016-01-31&_sort=applicationNumber&_pageSize=50',
                )}
            >
                https://data.epo.org/linked-data/doc/application?applicationAuthority.notation=EP&min-filingDate=2016-01-01&max-filingDate=2016-01-31&_sort=applicationNumber&_pageSize=50
            </a>
            <p>
                Note that if a sort of the data is specified on a request, the sort occurs on all of the selected
                resources, not just the data in the page retrieved. Because of the length of time it would take, it is
                not possible to, for example, sort all the EPO applications and retrieve them page by page.
            </p>
            <Terms />
        </Wrapper>
    );
};
export default APIOverviewPage;
